import { Button } from "@/components/ui/button-new";
import IconButton from "@/components/ui/icon-button";
import {
    CheckCircle,
    Save,
    ArrowLeft,
    ArrowRight,
    Undo,
    Trash,
} from "lucide-react";

export interface ActionBarProps {
    isReadOnly?: boolean;
    isLoading?: boolean;
    isSaving?: boolean;
    isApproving?: boolean;
    isDeleting?: boolean;
    onNavigate?: (direction: "next" | "prev") => void;
    onSave?: () => void;
    onApprove?: () => void;
    onRevokeApproval?: () => void;
    onDelete?: () => void;
    onUndo?: () => void;
    hasNext?: boolean;
    hasPrevious?: boolean;
    hasChanges?: boolean;
    canApprove?: boolean;
    canDelete?: boolean;
    canSave?: boolean;
}

export function ActionBar({
    isReadOnly = false,
    isLoading = false,
    isSaving = false,
    isApproving = false,
    isDeleting = false,
    onNavigate,
    onSave,
    onApprove,
    onRevokeApproval,
    onDelete,
    onUndo,
    hasNext,
    hasPrevious,
    hasChanges = false,
    canApprove = true,
    canDelete,
    canSave,
}: ActionBarProps) {
    return (
        <div className="-sticky top-0 z-10 p-0">
            <div className="px-0 py-0 flex justify-between items-center">
                <div>
                    {onNavigate && (
                        <IconButton
                            color="secondary"
                            size="md"
                            title="Previous (←)"
                            onClick={() => onNavigate("prev")}
                            disabled={isLoading || !hasPrevious}
                            className="mr-1"
                        >
                            <ArrowLeft className=" h-4 w-4" />
                        </IconButton>
                    )}
                </div>

                <div className="flex gap-1">
                    {onDelete && !isReadOnly && (
                        <Button
                            onClick={onDelete}
                            disabled={isDeleting || isLoading || !canDelete}
                            variant="outline"
                            size="sm"
                            className="text-md-error border-md-error hover:bg-md-error/10 hover:text-md-error"
                            loading={isDeleting}
                        >
                            <Trash className="h-4 w-4" />
                        </Button>
                    )}
                    {!isReadOnly && onUndo && (
                        <Button
                            onClick={onUndo}
                            size="sm"
                            variant="outline"
                            disabled={!hasChanges}
                        >
                            <Undo className="md:mr-1 h-4 w-4" />
                            <span className="hidden md:block">Undo</span>
                        </Button>
                    )}
                    {onSave && !isReadOnly && (
                        <Button
                            onClick={onSave}
                            disabled={isSaving || isLoading || !canSave}
                            size="sm"
                        >
                            <Save className="md:mr-1 h-4 w-4" />
                            <span className="hidden md:block">
                                {isSaving ? "Saving..." : "Save"}
                            </span>
                        </Button>
                    )}
                    {onApprove && !isReadOnly && (
                        <Button
                            onClick={onApprove}
                            disabled={isApproving || isLoading || !canApprove}
                            variant="default"
                            size="sm"
                        >
                            <CheckCircle className="mr-1 h-4 w-4" />
                            {isApproving ? "Approving..." : "Approve"}
                        </Button>
                    )}
                    {onRevokeApproval && isReadOnly && (
                        <Button
                            onClick={onRevokeApproval}
                            disabled={isApproving || isLoading}
                            variant="outline"
                            size="sm"
                            className="text-md-on-secondary-container border-md-secondary-container hover:bg-md-secondary-container/20"
                        >
                            Revoke Approval
                        </Button>
                    )}
                </div>
                <div>
                    {onNavigate && (
                        <IconButton
                            color="secondary"
                            size="md"
                            title="Next (→)"
                            onClick={() => onNavigate("next")}
                            disabled={isLoading || !hasNext}
                            className="ml-1"
                        >
                            <ArrowRight className="h-4 w-4" />
                        </IconButton>
                    )}
                </div>
            </div>
        </div>
    );
}
